import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AvailabilitySlotsPicker } from '@/components/AvailabilitySlotsPicker';
import { CUISINES } from '@/data/cuisines';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_IMAGES = 5;
const COMPRESSION_OPTIONS = {
  maxSizeMB: 1.2,          // Target ~1.2MB per image
  maxWidthOrHeight: 1920,  // Cap dimension so quality remains crisp on large screens
  useWebWorker: true,
  initialQuality: 0.85,
  fileType: 'image/jpeg'
};

const categories = ['Hot Meals', 'Baked Goods', 'Desserts', 'Sauces & Preserves', 'Meal Prep'];
const CUSTOM_CATEGORY_VALUE = '__custom__';
const dietaryOptions = ['Vegan', 'Vegetarian', 'Gluten-Free', 'Halal', 'Nut-Free', 'Dairy-Free'];
const fulfillmentOptions = ['Pickup', 'Delivery', 'Both'];

export const PostMealPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [customCategoryMode, setCustomCategoryMode] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    ingredients: '',
    dietary_tags: [],
    price: '',
    quantity: '',
    fulfillment_options: [],
    availability_dates: [],
    category: '',
    cuisine: '',
    location: user?.location || ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckbox = (field, value) => {
    const current = formData[field];
    if (current.includes(value)) {
      setFormData({ ...formData, [field]: current.filter(item => item !== value) });
    } else {
      setFormData({ ...formData, [field]: [...current, value] });
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    // Reset input so selecting the same file again re-triggers change
    e.target.value = '';

    if (files.length === 0) return;

    const remainingSlots = MAX_IMAGES - uploadedFiles.length;
    if (remainingSlots <= 0) {
      toast.error(`You can upload a maximum of ${MAX_IMAGES} photos`);
      return;
    }
    const filesToProcess = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      toast.warning(`Only the first ${remainingSlots} photo(s) will be added (max ${MAX_IMAGES} total)`);
    }

    setLoading(true);
    try {
      const uploadPromises = filesToProcess.map(async (file) => {
        // Compress image client-side before upload; falls back to original on failure
        let toUpload = file;
        try {
          const compressed = await imageCompression(file, COMPRESSION_OPTIONS);
          // Only use compressed version if it actually saved bytes
          toUpload = compressed.size < file.size ? compressed : file;
        } catch (compressionError) {
          console.warn('Compression failed, uploading original:', compressionError);
        }

        const fd = new FormData();
        fd.append('file', toUpload, toUpload.name || file.name);
        const { data } = await axios.post(`${API}/upload`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        return data;
      });

      const results = await Promise.all(uploadPromises);
      setUploadedFiles((prev) => [...prev, ...results]);
      toast.success(`${results.length} photo${results.length > 1 ? 's' : ''} uploaded`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload images');
    } finally {
      setLoading(false);
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/listings`, {
        ...formData,
        cuisine: formData.cuisine || null,
        price: parseFloat(formData.price),
        quantity: parseInt(formData.quantity)
      }, { withCredentials: true });

      if (uploadedFiles.length > 0) {
        await axios.put(`${API}/listings/${data.id}/images`, 
          uploadedFiles.map(f => f.url),
          { withCredentials: true }
        );
      }

      if (data.status === 'approved') {
        toast.success('Listing created and live!');
        navigate(`/listing/${data.id}`);
      } else {
        toast.success('Listing submitted — awaiting admin approval. You\'ll be notified when it goes live.', { duration: 6000 });
        navigate('/my-listings');
      }
    } catch (error) {
      console.error('Error creating listing:', error);
      toast.error('Failed to create listing');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && (!formData.title || !formData.description || !formData.category.trim())) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (step === 2 && (!formData.price || !formData.quantity)) {
      toast.error('Please fill in price and quantity');
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  return (
    <div style={{ backgroundColor: '#F9F6F0', minHeight: '100vh' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 
          className="text-4xl sm:text-5xl mb-2 tracking-tight" 
          style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
          data-testid="post-meal-heading"
        >
          Post a Meal
        </h1>
        <p className="mb-8" style={{ fontFamily: 'Manrope', color: '#5C5C5C', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.2em' }}>
          Step {step} of 4: {step === 1 ? 'Details' : step === 2 ? 'Pricing & Availability' : step === 3 ? 'Media' : 'Location'}
        </p>

        <div className="p-8 rounded-md border border-[#E5E0D8]" style={{ backgroundColor: '#FFFFFF' }} data-testid="post-meal-form">
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                  Dish Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Homemade Lasagna"
                  className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                  style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                  data-testid="title-input"
                />
              </div>

              <div>
                <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                  Category *
                </label>
                <select
                  name="category"
                  value={customCategoryMode ? CUSTOM_CATEGORY_VALUE : formData.category}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === CUSTOM_CATEGORY_VALUE) {
                      setCustomCategoryMode(true);
                      setFormData({ ...formData, category: '' });
                    } else {
                      setCustomCategoryMode(false);
                      setFormData({ ...formData, category: v });
                    }
                  }}
                  className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                  style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                  data-testid="category-select"
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  <option value={CUSTOM_CATEGORY_VALUE}>Type your own…</option>
                </select>
                {customCategoryMode && (
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    placeholder="e.g., Sushi Rolls, Fermented Foods, BBQ Platters"
                    maxLength={60}
                    autoFocus
                    className="w-full mt-2 px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                    style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                    data-testid="category-custom-input"
                  />
                )}
              </div>

              <div>
                <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                  Type of Cuisine
                </label>
                <select
                  name="cuisine"
                  value={formData.cuisine}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                  style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                  data-testid="cuisine-select"
                >
                  <option value="">Select a cuisine (optional)</option>
                  {CUISINES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <p className="mt-1" style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#5C5C5C' }}>
                  Helps buyers filter by the food's country/region of origin.
                </p>
              </div>

              <div>
                <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Tell people about your dish..."
                  className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                  style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                  data-testid="description-input"
                />
              </div>

              <div>
                <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                  Ingredients
                </label>
                <textarea
                  name="ingredients"
                  value={formData.ingredients}
                  onChange={handleChange}
                  rows={3}
                  placeholder="List main ingredients..."
                  className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                  style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                  data-testid="ingredients-input"
                />
              </div>

              <div>
                <label className="block mb-3" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                  Dietary Tags
                </label>
                <div className="flex flex-wrap gap-3">
                  {dietaryOptions.map((diet) => (
                    <label key={diet} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.dietary_tags.includes(diet)}
                        onChange={() => handleCheckbox('dietary_tags', diet)}
                        className="w-4 h-4"
                        data-testid={`dietary-tag-${diet.toLowerCase()}`}
                      />
                      <span style={{ fontFamily: 'Manrope', fontSize: '14px' }}>{diet}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                    Price per Portion ($) *
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="10.00"
                    className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                    style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                    data-testid="price-input"
                  />
                </div>

                <div>
                  <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                    Quantity Available *
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    min="1"
                    placeholder="5"
                    className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                    style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                    data-testid="quantity-input"
                  />
                </div>
              </div>

              <div>
                <label className="block mb-3" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                  Fulfillment Options
                </label>
                <div className="flex flex-wrap gap-3">
                  {fulfillmentOptions.map((option) => (
                    <label key={option} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.fulfillment_options.includes(option)}
                        onChange={() => handleCheckbox('fulfillment_options', option)}
                        className="w-4 h-4"
                        data-testid={`fulfillment-${option.toLowerCase()}`}
                      />
                      <span style={{ fontFamily: 'Manrope', fontSize: '14px' }}>{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <AvailabilitySlotsPicker
                  value={formData.availability_dates}
                  onChange={(slots) => setFormData({ ...formData, availability_dates: slots })}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <label style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                    Upload Food Images
                  </label>
                  <span
                    style={{ fontFamily: 'Manrope', fontSize: '12px', color: uploadedFiles.length >= MAX_IMAGES ? '#D95A41' : '#5C5C5C' }}
                    data-testid="image-count"
                  >
                    {uploadedFiles.length} / {MAX_IMAGES}
                  </span>
                </div>
                <div
                  className={`border-2 border-dashed rounded-md p-8 text-center transition-colors ${
                    uploadedFiles.length >= MAX_IMAGES
                      ? 'border-[#E5E0D8] opacity-60 cursor-not-allowed'
                      : 'border-[#E5E0D8] cursor-pointer hover:border-[#D95A41]'
                  }`}
                  onClick={() => uploadedFiles.length < MAX_IMAGES && document.getElementById('file-upload').click()}
                  data-testid="image-upload-area"
                >
                  <Upload size={48} className="mx-auto mb-4" style={{ color: '#5C5C5C', strokeWidth: 1.5 }} />
                  <p style={{ fontFamily: 'Manrope', color: '#5C5C5C', marginBottom: '8px' }}>
                    {uploadedFiles.length >= MAX_IMAGES
                      ? 'Photo limit reached — remove one to add more'
                      : 'Click to upload or drag and drop'}
                  </p>
                  <p style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#5C5C5C' }}>
                    Up to {MAX_IMAGES} photos · PNG or JPG · auto-compressed for fast loading
                  </p>
                  <input
                    id="file-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={uploadedFiles.length >= MAX_IMAGES}
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-input"
                  />
                </div>
              </div>

              {uploadedFiles.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="relative aspect-square rounded-md overflow-hidden border border-[#E5E0D8]">
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${file.url}`} 
                        alt={`Upload ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => removeFile(index)}
                        className="absolute top-2 right-2 p-1 rounded-full"
                        style={{ backgroundColor: 'rgba(26, 26, 26, 0.8)' }}
                        data-testid={`remove-image-${index}`}
                      >
                        <X size={16} style={{ color: '#FFFFFF' }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <div>
                <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
                  Pickup/Delivery Location *
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="City, State or specific address"
                  className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                  style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                  data-testid="location-input"
                />
              </div>

              <div className="p-4 rounded-md" style={{ backgroundColor: '#F2EFEB' }}>
                <h3 className="mb-2" style={{ fontFamily: 'Manrope', fontWeight: 600, fontSize: '16px' }}>
                  Review Your Listing
                </h3>
                <div className="space-y-1" style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#5C5C5C' }}>
                  <p><strong>Title:</strong> {formData.title}</p>
                  <p><strong>Category:</strong> {formData.category}</p>
                  <p><strong>Price:</strong> ${formData.price}</p>
                  <p><strong>Quantity:</strong> {formData.quantity}</p>
                  <p><strong>Images:</strong> {uploadedFiles.length} uploaded</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8 pt-6 border-t border-[#E5E0D8]">
            {step > 1 && (
              <Button 
                onClick={prevStep}
                variant="ghost"
                style={{ fontFamily: 'Manrope' }}
                data-testid="prev-step-btn"
              >
                Previous
              </Button>
            )}
            {step < 4 ? (
              <Button 
                onClick={nextStep}
                className="ml-auto rounded-md"
                style={{ backgroundColor: '#D95A41', fontFamily: 'Manrope', fontWeight: 600 }}
                data-testid="next-step-btn"
              >
                Next
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={loading}
                className="ml-auto rounded-md"
                style={{ backgroundColor: '#D95A41', fontFamily: 'Manrope', fontWeight: 600 }}
                data-testid="submit-listing-btn"
              >
                {loading ? 'Creating...' : 'Create Listing'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
